import { media_base_url } from "../constants";

/* DATA */

export const parseStringify = (value) => JSON.parse(JSON.stringify(value));

/* MEDIA */

export const MAX_PROFILE_IMAGE_SIZE = 5 * 1024 * 1024;
export const MAX_COVER_IMAGES = 1;
export const MAX_COVER_VIDEO_DURATION = 15;

export const MAX_POST_VIDEOS = 10;
export const MAX_POST_VIDEO_SIZE = 100 * 1024 * 1024;
export const MAX_POST_VIDEO_DURATION = 10 * 60;

export const IMAGE_TYPES = ["image/jpeg", "image/png", "image/webp"];
export const VIDEO_TYPES = ["video/mp4", "video/webm"];
export const AUDIO_TYPES = ["audio/webm", "audio/mp4", "audio/ogg"];
export const AUDIO_RECORDING_TYPES = ["audio/webm;codecs=opus", "audio/webm", "audio/mp4", "audio/ogg;codecs=opus"];

export const getMediaUrl = (content) => {
  if (!content) return "";
  if (/^(https?:\/\/|blob:|data:)/i.test(content)) return content;
  return `${media_base_url}/${content.replace(/^\/+/, "")}`;
};

export const isValidImage = (file) => IMAGE_TYPES.includes(file?.type);
export const isValidVideo = (file) => VIDEO_TYPES.includes(file?.type);
export const isValidAudio = (file) => AUDIO_TYPES.includes(file?.type);
export const isValidProfileImageSize = (file) => Boolean(file && file.size <= MAX_PROFILE_IMAGE_SIZE);
export const isValidCoverImageCount = (files) => Boolean(files && files.length <= MAX_COVER_IMAGES);

export const getVideoMetadata = (file) => new Promise((resolve, reject) => {
  const video = document.createElement("video");
  const url = URL.createObjectURL(file);
  let finished = false;

  const cleanup = () => {
    clearTimeout(timeout);
    video.removeAttribute("src");
    video.load();
    URL.revokeObjectURL(url);
  };

  const finish = (error, metadata) => {
    if (finished) return;
    finished = true;
    cleanup();
    if (error) reject(error);
    else resolve(metadata);
  };

  const timeout = setTimeout(() => finish(new Error("Video metadata timed out.")), 15000);

  video.preload = "metadata";
  video.onloadedmetadata = () => {
    if (!Number.isFinite(video.duration) || video.duration <= 0) return finish(new Error("Unable to determine video duration."));
    finish(null, { duration: video.duration, width: video.videoWidth, height: video.videoHeight, size: file.size });
  };

  video.onerror = () => finish(new Error("Unable to read this video."));
  video.src = url;
});

export const validatePostVideos = async (files = []) => {
  if (!files.length) return { valid: false, error: "Please select at least one video." };
  if (files.length > MAX_POST_VIDEOS) return { valid: false, error: `Maximum ${MAX_POST_VIDEOS} videos per post.` };

  const metadata = [];

  for (const file of files) {
    if (!(file instanceof File)) return { valid: false, error: "Invalid video file." };
    if (!isValidVideo(file)) return { valid: false, error: `Unsupported video: ${file.name}` };
    if (file.size > MAX_POST_VIDEO_SIZE) return { valid: false, error: `${file.name} exceeds the 100 MB limit.` };

    try {
      const info = await getVideoMetadata(file);
      if (info.duration > MAX_POST_VIDEO_DURATION) return { valid: false, error: `${file.name} exceeds the 10-minute limit.` };
      metadata.push({ name: file.name, ...info });
    } catch (error) {
      return { valid: false, error: `${file.name}: ${error.message}` };
    }
  }

  return { valid: true, metadata };
};

export const formatFileSize = (bytes) => {
  if (!bytes) return "0 B";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

/* TEXT */

export const truncateText = (text, limit = 200) => {
  if (!text) return "";
  if (text.length <= limit) return text;
  return `${text.slice(0, limit).trim()}...`;
};

export const shouldShowReadMore = (text, limit = 200) => Boolean(text && text.length > limit);

export const capitalize = (text) => {
  if (!text) return "";
  return `${text.charAt(0).toUpperCase()}${text.slice(1)}`;
};

export const getInitials = (firstName = "", lastName = "") => `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase();

/* URLS */

export const URL_REGEX = /(https?:\/\/[^\s]+|www\.[^\s]+)/gi;

export const containsUrl = (text) => {
  if (!text) return false;
  URL_REGEX.lastIndex = 0;
  return URL_REGEX.test(text);
};

export const normalizeUrl = (url) => {
  if (!url) return "";
  return /^https?:\/\//i.test(url) ? url : `https://${url}`;
};

export const getTextParts = (text) => {
  if (!text) return [];
  const regex = /(https?:\/\/[^\s]+|www\.[^\s]+)/gi;
  return text.split(regex).filter(Boolean).map((value) => ({ value, isUrl: /^(https?:\/\/|www\.)/i.test(value) }));
};

/* ERRORS */

export const getErrorMessage = (error, fallback = "Something went wrong. Please try again.") => error?.response?.data?.message || error?.response?.data?.error || error?.data?.message || error?.message || fallback;

/* NUMBERS */

export const formatCount = (count = 0) => {
  const number = Number(count) || 0;
  if (number < 1000) return `${number}`;
  if (number < 1000000) return `${(number / 1000).toFixed(number >= 10000 ? 0 : 1).replace(".0", "")}K`;
  if (number < 1000000000) return `${(number / 1000000).toFixed(number >= 10000000 ? 0 : 1).replace(".0", "")}M`;
  return `${(number / 1000000000).toFixed(number >= 10000000000 ? 0 : 1).replace(".0", "")}B`;
};

/* SEARCH */

export const SEARCH_DEBOUNCE = 400;

export const normalizeSearch = (value) => value?.trim().toLowerCase() || "";

/* USER */

export const getFullName = (user) => [user?.firstname, user?.lastname].filter(Boolean).join(" ");

export const isSameUser = (firstId, secondId) => {
  if (firstId === undefined || firstId === null || firstId === "" || secondId === undefined || secondId === null || secondId === "") return false;
  return String(firstId) === String(secondId);
};

/* ARRAYS */

export const removeDuplicatesById = (items = []) => Array.from(new Map(items.map((item) => [item?.id, item])).values());

export const removeItemById = (items = [], id, key = "id") => items.filter((item) => Number(item?.[key]) !== Number(id));

/* DATE */

export const formatDate = (date) => {
  if (!date) return "";
  return new Intl.DateTimeFormat("en-US", { month: "short", day: "numeric", year: "numeric" }).format(new Date(date));
};